import React, {useContext, useEffect, useState} from 'react';
import Button from '@material-ui/core/Button';
import ReplayIcon from '@material-ui/icons/Replay';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import DeleteIcon from '@material-ui/icons/Delete';
import {makeStyles} from '@material-ui/core/styles';

import AutoStreamTrackAddDialog from '../AutoStreamTrackAddDialog';

import uuidv4 from '../../../utils/uuidv4';
import _firebaseContext from '../../../contexts/firebaseContext';
import _appContext from '../../../contexts/appContext';
import {storageRef} from '../../Fire';

const AutoStreamTracksPageContent = () => {
    const classes = useStyles();
    const firebaseContext = useContext(_firebaseContext);
    const appContext = useContext(_appContext);

    const [changed, setChanged] = useState(false);
    const [autoRecords, setAutoRecords] = useState([]);

    const [newRecordFile, setNewRecordFile] = useState(undefined);
    const [newRecordFileUrl, setNewRecordFileUrl] = useState('');
    const [newRecordFileDuration, setNewRecordFileDuration] = useState(undefined);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [trackByUrlName, setTrackByUrlName] = useState('');
    const [isAddTrackDialogVisible, setIsAddTrackDialogVisible] = useState(false);

    const currentAudioPlayer = new Audio();
    const audioElement = document.createElement('AUDIO');

    const fetchAutoTracks = async () => {
        const autoTracksFetchResult = await firebaseContext.getAutoTracks();
        if (!autoTracksFetchResult.error) {
            setAutoRecords(autoTracksFetchResult.autoRecords);
            setChanged(false);
        }
    }

    useEffect(() => {
        (async () => {
            appContext.startLoading();
            await fetchAutoTracks();
            appContext.endLoading();
        })();
    }, []);

    const reloadFromDatabase = async () => {
        appContext.startLoading();
        await fetchAutoTracks();
        appContext.endLoading();
    }

    const saveToDatabase = async () => {
        appContext.startLoading();
        const savedTrack = await firebaseContext.saveAutoTracks(autoRecords);
        appContext.snackbar.showSnackbarMsg(savedTrack.message);
        if (!savedTrack.error) {
            setChanged(false);
        }
        appContext.endLoading();
    }

    const handleAddTrackFile = (event) => {
        const file = event.target.files[0];

        setNewRecordFile(file);
        setNewRecordFileUrl('');
        handleDurationOfAudioFile(file);
    }

    const handleAutoTrackUrlChange = (event) => {
        const fileUrl = event.target.value;

        setNewRecordFileUrl(fileUrl);
        setNewRecordFile(undefined);
        handleDurationOfAudioFileUrl(fileUrl);
    }

    const handleDurationOfAudioFile = (file) => {
        audioElement.src = URL.createObjectURL(file);
        audioElement.addEventListener('canplaythrough', () =>
            setNewRecordFileDuration(audioElement.duration)
        )
    }

    const handleDurationOfAudioFileUrl = (fileUrl) => {
        audioElement.src = fileUrl;
        setNewRecordFileDuration(undefined);
        audioElement.addEventListener('canplaythrough', () =>
            setNewRecordFileDuration(audioElement.duration)
        )
    }

    const getNewRecordDurationRepresent = (showInTable = false) => newRecordFileDuration ?
        `${Math.floor(newRecordFileDuration / 60)}:${Math.floor(newRecordFileDuration % 60)}` :
        showInTable ? '-' : 'Не загружено';

    const handleNewRecordAdd = async () => {
        appContext.startLoading();
        if (newRecordFileUrl && trackByUrlName) {
            const trackCDNUrl = await firebaseContext.moveTrackFile(newRecordFileUrl, trackByUrlName);

            setAutoRecords([{
                name: trackByUrlName,
                url: trackCDNUrl,
                duration: getNewRecordDurationRepresent(true)
            }, ...autoRecords]);
            setNewRecordFileUrl('');
            setTrackByUrlName('');
            setNewRecordFileDuration(undefined);
            setChanged(true);
            appContext.endLoading();
        }

        if (!(newRecordFile instanceof File)) {
            appContext.endLoading();
            return;
        }

        setUploadingFile(true);

        try {
            const storageId = uuidv4();
            const fileRef = storageRef.child(`sounds/${storageId}`);
            const recordName = newRecordFile.name ? newRecordFile.name : '';

            const newTrackUrl = await fileRef
                .put(newRecordFile)
                .then(() => fileRef.getDownloadURL());

            const trackCDNUrl = await firebaseContext.moveTrackFile(newTrackUrl, recordName);

            setAutoRecords([{
                name: recordName,
                url: trackCDNUrl,
                duration: getNewRecordDurationRepresent(true)
            }, ...autoRecords]);
            setNewRecordFile(undefined);
            setNewRecordFileDuration(undefined);
            setChanged(true);
        } finally {
            setUploadingFile(false);
            appContext.endLoading();
        }
    }

    const handleNewRecordRemove = (index) => {
        const newAutoRecord = [...autoRecords];
        newAutoRecord.splice(index, 1);
        setAutoRecords(newAutoRecord);
        setChanged(true);
    }

    const playAudio = (trackUrl) => {
        currentAudioPlayer.src = trackUrl;
        currentAudioPlayer.addEventListener('canplaythrough', () =>
            currentAudioPlayer.play()
        )
    }

    const pauseAudio = () => {
        currentAudioPlayer.pause();
    }

    const dataChangeHandler = (event, index, dataField) => {
        const currentTrack = autoRecords[index];
        currentTrack[dataField] = event.target.value;
        const records = [...autoRecords];
        records[index] = currentTrack;
        setAutoRecords(records);
        setChanged(true);
    }

    const handleTrackByUrlNameChange = event => {
        pauseAudio();
        setTrackByUrlName(event.target.value);
    }

    const addTrackDialogVisibilityHandler = () => {
        pauseAudio();
        setIsAddTrackDialogVisible(!isAddTrackDialogVisible);

        if (!isAddTrackDialogVisible) {
            setNewRecordFileUrl('');
            setNewRecordFile(undefined);
            setTrackByUrlName('');
            setNewRecordFileDuration(undefined);
        }
    }

    return (
        <div className={classes.container}>
            <div>
                <Button onClick={saveToDatabase} color='primary' disabled={!changed}>
                    Сохранить
                </Button>
                <Button onClick={reloadFromDatabase} color='primary' disabled={!changed}>
                    <ReplayIcon/>
                </Button>
            </div>

            <div>
                <Button color='primary' onClick={addTrackDialogVisibilityHandler}>
                    Добавить трек
                </Button>
            </div>

            <div className={classes.tableContainer}>
                {autoRecords.map((record, index) =>
                    <Grid container direction={'column'} key={`record${index}`} className={classes.itemWrapper}>
                        <Grid container direction={'row'} spacing={2}>
                            <Grid item xs={3}>
                                <TextField
                                    disabled={true}
                                    fullWidth
                                    label='Наименование'
                                    type='text'
                                    value={record.name}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    disabled={true}
                                    fullWidth
                                    label='Автоматический трек'
                                    type='text'
                                    value={record.url}
                                    onChange={event => dataChangeHandler(event, index, 'url')}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    disabled={true}
                                    fullWidth
                                    label='Длительность'
                                    type='text'
                                    value={record.duration}
                                    onChange={event => dataChangeHandler(event, index, 'duration')}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={6}>
                            <Button disabled={!record.url} onClick={() => playAudio(record.url)}>Play</Button>
                            <Button disabled={!record.url} onClick={() => pauseAudio()}>Stop</Button>
                            <Button onClick={() => handleNewRecordRemove(index)}><DeleteIcon/></Button>
                        </Grid>
                    </Grid>)}
            </div>
            <AutoStreamTrackAddDialog
                isDialogVisible={isAddTrackDialogVisible}
                dialogCloseHandler={addTrackDialogVisibilityHandler}
                handleAddTrackFile={handleAddTrackFile}
                newRecordFileUrl={newRecordFileUrl}
                handleAutoTrackUrlChange={handleAutoTrackUrlChange}
                newRecordFile={newRecordFile}
                trackByUrlName={trackByUrlName}
                handleTrackByUrlNameChange={handleTrackByUrlNameChange}
                getNewRecordDurationRepresent={getNewRecordDurationRepresent}
                playAudio={playAudio}
                pauseAudio={pauseAudio}
                handleNewRecordAdd={handleNewRecordAdd}
                uploadingFile={uploadingFile}
            />
        </div>
    )
}

const useStyles = makeStyles({
    container: {
        marginTop: 15
    },
    tableContainer: {
        marginLeft: 8,
        marginTop: 20
    },
    itemWrapper: {
        marginBottom: 10
    },
    trackByUrlField: {
        marginRight: 15,
        width: 180,
        minWidth: 180
    },
    listItem: {
        paddingLeft: 8,
        paddingRight: 8
    }
});

export default AutoStreamTracksPageContent;
